import { Box, Button, Tooltip, useTheme } from "@mui/material";
import { MapButton } from "./mapUtility/MapButton";
import { tokens } from "../../theme/theme";
import SettingsIcon from "@mui/icons-material/Settings";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import TerrainIcon from "@mui/icons-material/Terrain";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import SsidChartIcon from "@mui/icons-material/SsidChart";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import AnimationIcon from "@mui/icons-material/Animation";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import LineWeightIcon from "@mui/icons-material/LineWeight";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import { MapFilter } from "./mapUtility/MapFilter";
import { MapSearch } from "./mapUtility/MapSearch";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import { useMap } from "react-leaflet";
import { useTranslation } from "react-i18next";
import { MapType } from "./mapUtility/MapType";

export const MapUtility = ({
  setFilter,
  filter,
  center,
  preview,
  hasElevation,
  showElevation,
  setShowElevation,
  elevationMetric,
  setElevationMetric,
  onUndo,
  canUndo,
  onRedo,
  canRedo,
  flowDisplay,
  setFlowDisplay,
  arrowSize,
  setArrowSize,
  isResultView,
  edgeMetric,
  setEdgeMetric,
  showMetrics,
  setShowMetrics,
  showOffset,
  setShowOffset,
  profileActive,
  onToggleProfile,
}) => {
  const map = useMap();
  const { t } = useTranslation();
  const isFlowMetric = edgeMetric && edgeMetric !== "diameter";
  // Flow-direction button cycles through these three modes in order.
  const FLOW_MODES = ["animated", "arrows", "none"];
  const flowConfig = {
    animated: { Icon: AnimationIcon, tooltip: t("Flow: animated"), active: true },
    arrows: { Icon: ArrowRightAltIcon, tooltip: t("Flow: arrows"), active: true },
    none: { Icon: ArrowRightAltIcon, tooltip: t("Flow: off"), active: false },
  };
  const flowCfg = flowConfig[flowDisplay] || flowConfig.none;
  // bounds/step for the adjustable edge arrow size (px)
  const ARROW_SIZE_MIN = 6;
  const ARROW_SIZE_MAX = 30;
  const ARROW_SIZE_STEP = 2;
  function recenterMap() {
    map.setView(center);
  }

  return (
    <Box
      className=" absolute top-2 left-2 flex flex-col items-start gap-3"
      sx={{ zIndex: "9999" }}
    >
      {!preview && (onUndo || onRedo) && (
        <Box className="flex flex-row gap-3">
          {onUndo && (
            <MapButton
              tooltip={t("Undo")}
              Icon={UndoIcon}
              onClick={onUndo}
              sx={canUndo ? {} : { opacity: 0.4, pointerEvents: "none" }}
            />
          )}
          {onRedo && (
            <MapButton
              tooltip={t("Redo")}
              Icon={RedoIcon}
              onClick={onRedo}
              sx={canRedo ? {} : { opacity: 0.4, pointerEvents: "none" }}
            />
          )}
        </Box>
      )}

      {(!preview || preview.search) && <MapSearch />}

      {(!preview || preview.filter) && (
        <MapButton
          tooltip="Filter"
          Icon={FilterAltIcon}
          popover={<MapFilter setFilter={setFilter} filter={filter} />}
        />
      )}
      {(!preview || preview.recenter) && (
        <MapButton
          tooltip="Recenter Map"
          Icon={CenterFocusStrongIcon}
          onClick={recenterMap}
        />
      )}

      {!preview && isResultView && setFlowDisplay && (
        <Box className="flex flex-row gap-3">
          <MapButton
            tooltip={flowCfg.tooltip}
            Icon={flowCfg.Icon}
            onClick={() =>
              setFlowDisplay(
                (mode) =>
                  FLOW_MODES[
                    (FLOW_MODES.indexOf(mode) + 1) % FLOW_MODES.length
                  ]
              )
            }
            color={flowCfg.active ? "white" : undefined}
            sx={flowCfg.active ? { backgroundColor: "orange" } : {}}
          />
          {setArrowSize && flowDisplay === "arrows" && (
            // +/- stacked vertically, together the same height as the arrow
            // button next to them, for adjusting the edge arrow size
            <Box className="flex flex-col">
              <ArrowSizeButton
                tooltip={t("Larger arrows")}
                Icon={AddIcon}
                disabled={arrowSize >= ARROW_SIZE_MAX}
                onClick={() =>
                  setArrowSize((value) =>
                    Math.min((value ?? 10) + ARROW_SIZE_STEP, ARROW_SIZE_MAX)
                  )
                }
              />
              <ArrowSizeButton
                tooltip={t("Smaller arrows")}
                Icon={RemoveIcon}
                disabled={arrowSize <= ARROW_SIZE_MIN}
                onClick={() =>
                  setArrowSize((value) =>
                    Math.max((value ?? 10) - ARROW_SIZE_STEP, ARROW_SIZE_MIN)
                  )
                }
              />
            </Box>
          )}
        </Box>
      )}

      {/* Result view: edge thickness metric (diameter <-> flow), and for flow
          the year (first / final) is selectable via a second toggle. */}
      {isResultView && setEdgeMetric && (
        <Box className="flex flex-row gap-3">
          <MapButton
            tooltip={
              isFlowMetric ? t("Thickness: flow") : t("Thickness: diameter")
            }
            Icon={LineWeightIcon}
            onClick={() =>
              setEdgeMetric(isFlowMetric ? "diameter" : "final_year_pipe_flow")
            }
            color={isFlowMetric ? "white" : undefined}
            sx={isFlowMetric ? { backgroundColor: "orange" } : {}}
          />
          {isFlowMetric && (
            <MapButton
              tooltip={
                edgeMetric === "final_year_pipe_flow"
                  ? t("Final year")
                  : t("First year")
              }
              Icon={CalendarMonthIcon}
              onClick={() =>
                setEdgeMetric(
                  edgeMetric === "final_year_pipe_flow"
                    ? "first_year_pipe_flow"
                    : "final_year_pipe_flow"
                )
              }
            />
          )}
        </Box>
      )}

      {hasElevation && setShowElevation && (
        <Box className="flex flex-row gap-3">
          <MapButton
            tooltip={t("Visualize Elevation")}
            Icon={TerrainIcon}
            onClick={() => setShowElevation((value) => !value)}
            color={showElevation ? "white" : undefined}
            sx={showElevation ? { backgroundColor: "orange" } : {}}
          />

          {/* Result view: switch the elevation coloring between ground elevation
              (terrain) and the calculated invert elevation, placed next to the
              visualize-elevation toggle */}
          {showElevation && isResultView && setElevationMetric && (
            <MapButton
              tooltip={
                elevationMetric === "invert"
                  ? t("Invert Elevation")
                  : t("Ground Elevation")
              }
              Icon={SwapVertIcon}
              onClick={() =>
                setElevationMetric((metric) =>
                  metric === "invert" ? "ground" : "invert"
                )
              }
            />
          )}
        </Box>
      )}

      {!preview && hasElevation && onToggleProfile && (
        <MapButton
          tooltip={t("Elevation profile")}
          Icon={SsidChartIcon}
          onClick={onToggleProfile}
          color={profileActive ? "white" : undefined}
          sx={profileActive ? { backgroundColor: "orange" } : {}}
        />
      )}

      {/* Result-view Kennzahlen (metrics) panel toggle */}
      {!preview && isResultView && setShowMetrics && (
        <MapButton
          tooltip={t("Metrics")}
          Icon={AssessmentIcon}
          onClick={() => setShowMetrics((value) => !value)}
          color={showMetrics ? "white" : undefined}
          sx={showMetrics ? { backgroundColor: "orange" } : {}}
        />
      )}

      {/* Result-view "Offset": nudges coincident nodes (base-grid edge cuts)
          apart so the cut becomes visible. Purely visual. */}
      {!preview && isResultView && setShowOffset && (
        <MapButton
          tooltip={t("Offset")}
          Icon={CallSplitIcon}
          onClick={() => setShowOffset((value) => !value)}
          color={showOffset ? "white" : undefined}
          sx={showOffset ? { backgroundColor: "orange" } : {}}
        />
      )}

      <MapType preview={preview} />
    </Box>
  );
};

// Compact +/- button used to adjust the edge arrow size. Sized so two of them
// stacked equal the height of a regular MapButton next to them.
const ArrowSizeButton = ({ Icon, tooltip, onClick, disabled }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  return (
    <Tooltip title={tooltip} placement="right">
      <span>
        <Button
          onClick={onClick}
          disabled={disabled}
          variant="contained"
          sx={{
            minWidth: "1px",
            width: "35px",
            height: "16px",
            padding: 0,
            borderRadius: "5px",
          }}
        >
          <Icon
            sx={{
              color: disabled ? colors.contrast[300] : colors.primary[400],
              fontSize: "14px",
            }}
          />
        </Button>
      </span>
    </Tooltip>
  );
};
